import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '../../../filters/translate.pipe';
import { InputDatepickerNewComponent } from '../../input-datepicker-new/input-datepicker-new.component';
import moment from "moment/moment";

@Component({
	standalone: true,
	selector: 'toolkit-history',
	templateUrl: './toolkit-history.component.html',
	imports: [CommonModule, FormsModule, TranslatePipe, InputDatepickerNewComponent]
})
export class ToolkitHistoryComponent implements OnInit {
	@Input() vm: any;

	truncateDescription(value: any, limit = 80): string {
		const text = (value || '').toString().trim();
		if (!text) return '';
		return text.length > limit ? text.slice(0, limit) + '...' : text;
	}

	private userLocale(): string | undefined {
		const raw = (this.vm?.user?.deviceLanguage || this.vm?.user?.language || '').toString().trim();
		if (!raw) return undefined;
		return raw.replace('_', '-');
	}

	ngOnInit(): void {
		if (!this.vm) return;
		this.vm.historyClientId = this.vm.historyClientId ?? 'ALL';
		this.vm.historyType = this.vm.historyType ?? 'ALL';
		this.vm.historyStatus = this.vm.historyStatus ?? 'ALL';
		this.vm.historyItemId = this.vm.historyItemId ?? 'ALL';
		this.vm.historySearch = this.vm.historySearch ?? '';
		this.vm.historyPage = this.vm.historyPage ?? 1;
		this.vm.historyPageSize = this.vm.historyPageSize ?? 12;
		this.vm.historyDateRangeModel = {
			start: moment().subtract(3, 'months').toDate(),
			end: moment(new Date()).toDate()
		};
		this.vm.historyDateRangeMin = new Date(2020, 0, 1);
	}

	reset() {
		this.vm.historyClientId = 'ALL';
		this.vm.historyType = 'ALL';
		this.vm.historyStatus = 'ALL';
		this.vm.historyItemId = 'ALL';
		this.vm.historySearch = '';
		this.vm.historyPage = 1;
		this.vm.historyDateRangeModel = {
			start: moment().subtract(3, 'months').toDate(),
			end: moment(new Date()).toDate()
		};
	}

	private inDateRange(row: any): boolean {
		const m = this.vm?.historyDateRangeModel;
		const startRaw = m?.start || null;
		const endRaw = m?.end || null;
		if (!startRaw && !endRaw) return true;

		const dt = row?.dateTime ? new Date(row.dateTime) : null;
		if (!dt || !isFinite(dt.getTime())) return false;
		const day = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();

		if (startRaw) {
			const f = new Date(startRaw);
			if (isFinite(f.getTime())) {
				const ft = new Date(f.getFullYear(), f.getMonth(), f.getDate()).getTime();
				if (day < ft) return false;
			}
		}
		if (endRaw) {
			const t = new Date(endRaw);
			if (isFinite(t.getTime())) {
				const tt = new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime();
				if (day > tt) return false;
			}
		}
		return true;
	}

	private matchesSearch(row: any, q: string): boolean {
		if (!q) return true;
		const hay = `${row?.client || ''} ${row?.item || ''} ${row?.type || ''} ${row?.status || ''}`.toLowerCase();
		return hay.includes(q);
	}

	get filteredRows(): any[] {
		const rows: any[] = Array.isArray(this.vm?.historyRows) ? this.vm.historyRows : [];
		const q = (this.vm?.historySearch || '').toString().trim().toLowerCase();
		const clientId = (this.vm?.historyClientId || 'ALL').toString();
		const type = (this.vm?.historyType || 'ALL').toString();
		const status = (this.vm?.historyStatus || 'ALL').toString();
		const itemId = (this.vm?.historyItemId || 'ALL').toString();

		const out = rows.filter((r) => {
			if (!r) return false;
			if (!this.matchesSearch(r, q)) return false;
			if (!this.inDateRange(r)) return false;
			if (type !== 'ALL') {
				if (type === 'TOOLKIT' && r.type !== 'toolkit') return false;
				if (type === 'SURVEY' && r.type !== 'survey') return false;
			}
			if (status !== 'ALL') {
				if ((r.status || '').toString() !== status.toLowerCase()) return false;
			}
			if (itemId !== 'ALL') {
				const raw = r?.raw;
				const key = r?.type === 'survey' ? `survey:${raw?.assessmentId}` : `toolkit:${raw?.resourceId}`;
				if (key !== itemId) return false;
			}
			if (clientId !== 'ALL') {
				const raw = r?.raw;
				const cid = raw?.clientUserId != null ? raw.clientUserId.toString() : '';
				if (cid !== clientId) return false;
			}
			return true;
		});

		return out;
	}

	get pagedRows(): any[] {
		const page = Number(this.vm?.historyPage || 1);
		const size = Number(this.vm?.historyPageSize || 12);
		const start = (page - 1) * size;
		return this.filteredRows.slice(start, start + size);
	}

	get totalPages(): number {
		const size = Number(this.vm?.historyPageSize || 12);
		if (size <= 0) return 1;
		return Math.max(1, Math.ceil(this.filteredRows.length / size));
	}

	prevPage() {
		this.vm.historyPage = Math.max(1, Number(this.vm.historyPage || 1) - 1);
	}

	nextPage() {
		this.vm.historyPage = Math.min(this.totalPages, Number(this.vm.historyPage || 1) + 1);
	}

	summaryCounts(): any {
		const rows = this.filteredRows;
		const completed = rows.filter((r) => r?.status === 'completed').length;
		const overdue = rows.filter((r) => r?.status === 'overdue').length;
		const cancelled = rows.filter((r) => r?.status === 'cancelled').length;
		const total = rows.length;
		const surveysCompleted = rows.filter((r) => r?.type === 'survey' && r?.status === 'completed').length;
		const rate = total > 0 ? Math.round((completed / total) * 100) : 0;
		return { completed, overdue, cancelled, total, surveysCompleted, rate };
	}

	dateLabel(row: any): { day: string; time: string } {
		const dt = row?.dateTime ? new Date(row.dateTime) : null;
		if (!dt || !isFinite(dt.getTime())) return { day: (row?.date || '').toString(), time: '—' };
		const locale = this.userLocale();
		const day = dt.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
		const time = dt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
		return { day, time };
	}

	statusPill(row: any): { cls: string; key: string } {
		const s = (row?.status || '').toString();
		if (s === 'completed') return { cls: 'pill-done', key: 'Toolkit.History.Status.Completed' };
		if (s === 'overdue') return { cls: 'pill-overdue', key: 'Toolkit.History.Status.Overdue' };
		if (s === 'cancelled') return { cls: 'pill-cancelled', key: 'Toolkit.History.Status.Cancelled' };
		return { cls: 'pill-pending', key: 'Toolkit.History.Status.Pending' };
	}

	typeBadge(row: any): { cls: string; key: string } {
		if (row?.type === 'survey') return { cls: 'type-survey', key: 'Toolkit.History.Type.Survey' };
		return { cls: 'type-toolkit', key: 'Toolkit.History.Type.Exercise' };
	}

	action(row: any): { key: string; muted: boolean } {
		const status = (row?.status || '').toString();
		if (status === 'cancelled') return { key: 'Toolkit.Common.Dash', muted: true };
		if (status !== 'completed') return { key: 'Toolkit.Common.Dash', muted: true };
		return { key: 'Toolkit.History.Action.View', muted: false };
	}

	canOpen(row: any): boolean {
		return !this.action(row).muted;
	}

	open(row: any) {
		if (!this.canOpen(row)) return;
		const fn = this.vm?.openHistoryRow;
		if (typeof fn === 'function') fn(row);
	}

}
